import { PlusIcon, BadgeIcon, ArrowUpDown, Circle } from "lucide-react"
import { useMemo, useState } from "react"
import { Button, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../../components/ui"
import { Listing } from "src/lib/types"
import Page from "../../components/page/page"
import { useListingContext } from "../../hooks/use-listings"
import { useIsMobile } from "../../hooks/use-mobile"
import { EnhancedListingCard } from "./enhanced-listing-card"
import { RecordSaleDialog } from "./record-sale-dialog"
import { NewListingWizard } from "./new-listing-wizard"
import { toast } from "sonner"
import { FilterPill, SearchInput } from "../../components/molecules"
import { useSubscriptionTooling } from "../../hooks/use-subscription"
import { SubscriptionTypeEnum } from "../../lib/enums"
import SubscriptionGate from "../../components/subscription/SubscriptionGate"
import CollapsibleSection from "../../components/ui/collapsible-section"

export const ListingDashboard = () => {
  const { listings, updateListing, deleteListing } = useListingContext()
  const { hasAccess } = useSubscriptionTooling()
  const isMobile = useIsMobile()
  const hasListingAccess = hasAccess(SubscriptionTypeEnum.Premium)

  const [showListingWizard, setShowListingWizard] = useState(false)
  const [showRecordSaleDialog, setShowRecordSaleDialog] = useState(false)
  const [showSoldOut, setShowSoldOut] = useState(false)
  const [showActive, setShowActive] = useState(true)
  const [showArchived, setShowArchived] = useState(false)
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [sortBy, setSortBy] = useState<string>("date-desc")

  const listingItems = useMemo(() => {
    let filtered = listings.filter((li) => {
      // Filter by status
      if (li.status === 'archived' && !showArchived) return false

      // "Sold Out" means quantityAvailable is 0
      const isSoldOut = li.quantityAvailable === 0
      if (isSoldOut && !showSoldOut) return false

      // "Active" means quantityAvailable > 0 and not archived
      const isActive = li.quantityAvailable > 0 && li.status !== 'archived'
      if (isActive && !showActive) return false

      // Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        const matchesTitle = li.title?.toLowerCase().includes(query)
        const matchesDescription = li.description?.toLowerCase().includes(query)
        const matchesPlatform = li.platform?.toLowerCase().includes(query)
        if (!matchesTitle && !matchesDescription && !matchesPlatform) return false
      }

      return true
    })

    // Sort the filtered results
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0)
        case 'date-asc':
          return (a.createdAt?.toMillis() || 0) - (b.createdAt?.toMillis() || 0)
        case 'price-high':
          return (b.price || 0) - (a.price || 0)
        case 'price-low':
          return (a.price || 0) - (b.price || 0)
        case 'title-asc':
          return (a.title || '').localeCompare(b.title || '')
        case 'title-desc':
          return (b.title || '').localeCompare(a.title || '')
        default:
          return 0
      }
    })

    return filtered
  }, [showSoldOut, showActive, showArchived, listings, searchQuery, sortBy])

  // Calculate counts for each status
  const statusCounts = useMemo(() => {
    return {
      active: listings.filter(l => l.quantityAvailable > 0 && l.status !== 'archived').length,
      soldOut: listings.filter(l => l.quantityAvailable === 0).length,
      archived: listings.filter(l => l.status === 'archived').length,
    }
  }, [listings])

  const handleRecordSale = (listing: Listing) => {
    setSelectedListing(listing)
    setShowRecordSaleDialog(true)
  }

  const handleEdit = (listing: Listing) => {
    setSelectedListing(listing)
    setShowListingWizard(true)
  }

  const handleArchive = async (listing: Listing) => {
    const newStatus = listing.status === 'archived' ? 'active' : 'archived'
    await updateListing({
      ...listing,
      status: newStatus
    })
    toast.success(`Listing ${newStatus === 'archived' ? 'archived' : 'unarchived'}`)
  }

  const handleDelete = async (listing: Listing) => {
    if (window.confirm(`Are you sure you want to delete "${listing.title}"?`)) {
      await deleteListing(listing.id)
      toast.success('Listing deleted')
    }
  }

  return (
    <Page title="Listings">
      <div className="relative px-6">
        {/* Toolbar - Always visible New Listing button */}
        <div className="sticky top-0 z-50 -mx-6 backdrop-blur-lg bg-white/80 dark:bg-neutral900/80 border-b border-neutral200/50 dark:border-neutral700/50">
          {!isMobile && (
            <div className="px-6 pt-6 pb-6">
              <div className="max-w-7xl mx-auto space-y-4">
                {/* Desktop: Always visible toolbar */}
                <div className="flex gap-3 items-center">
                  <SubscriptionGate
                    requiredTier={SubscriptionTypeEnum.Premium}
                    mode="disable"
                    upgradeMessage="Upgrade to Premium to create new listings"
                  >
                    <Button
                      variant="primaryGlow"
                      size="lg"
                      onClick={() => {
                        setSelectedListing(null)
                        setShowListingWizard(true)
                      }}
                    >
                      <PlusIcon stroke="currentColor" width={20} height={20} />
                      <span className="font-semibold">New Listing</span>
                    </Button>
                  </SubscriptionGate>

                  <SearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search listings..."
                    className="flex-1"
                  />

                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="backdrop-blur-md bg-white/60 dark:bg-neutral800/60 border-2 border-neutral200/50 dark:border-neutral700/50 hover:border-neutral300 dark:hover:border-neutral600 w-[180px]">
                      <ArrowUpDown className="mr-2 h-4 w-4 text-neutral500" />
                      <SelectValue placeholder="Sort by..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Sort By</SelectLabel>
                        <SelectItem value="date-desc">Newest First</SelectItem>
                        <SelectItem value="date-asc">Oldest First</SelectItem>
                        <SelectItem value="price-high">Price: High to Low</SelectItem>
                        <SelectItem value="price-low">Price: Low to High</SelectItem>
                        <SelectItem value="title-asc">Title: A to Z</SelectItem>
                        <SelectItem value="title-desc">Title: Z to A</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-3 flex-wrap">
                  <FilterPill
                    label="Active"
                    count={statusCounts.active}
                    isActive={showActive}
                    onClick={() => setShowActive(prev => !prev)}
                    variant="primary"
                    icon={<Circle className="fill-current" width={8} height={8} />}
                  />
                  <FilterPill
                    label="Sold Out"
                    count={statusCounts.soldOut}
                    isActive={showSoldOut}
                    onClick={() => setShowSoldOut(prev => !prev)}
                    variant="success"
                    icon={<Circle className="fill-current" width={8} height={8} />}
                  />
                  <FilterPill
                    label="Archived"
                    count={statusCounts.archived}
                    isActive={showArchived}
                    onClick={() => setShowArchived(prev => !prev)}
                    variant="neutral"
                    icon={<Circle className="fill-current" width={8} height={8} />}
                  />
                </div>
              </div>
            </div>
          )}

          {isMobile && (
            <div>
              {/* Mobile: New Listing button always visible */}
              <div className="px-6 pt-4 pb-3">
                <SubscriptionGate
                  requiredTier={SubscriptionTypeEnum.Premium}
                  mode="disable"
                  upgradeMessage="Upgrade to Premium to create new listings"
                >
                  <Button
                    variant="primaryGlow"
                    size="lg"
                    className="w-full"
                    onClick={() => {
                      setSelectedListing(null)
                      setShowListingWizard(true)
                    }}
                  >
                    <PlusIcon stroke="currentColor" width={20} height={20} />
                    <span className="font-semibold">New Listing</span>
                  </Button>
                </SubscriptionGate>
              </div>

              {/* Mobile: Collapsible filters and search */}
              <CollapsibleSection title="Filters & Search" defaultOpen={false} className="border-t">
                <div className="space-y-4">
                  <SearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search listings..."
                    className="w-full"
                  />

                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="backdrop-blur-md bg-white/60 dark:bg-neutral800/60 border-2 border-neutral200/50 dark:border-neutral700/50 hover:border-neutral300 dark:hover:border-neutral600 w-full">
                      <ArrowUpDown className="mr-2 h-4 w-4 text-neutral500" />
                      <SelectValue placeholder="Sort by..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Sort By</SelectLabel>
                        <SelectItem value="date-desc">Newest First</SelectItem>
                        <SelectItem value="date-asc">Oldest First</SelectItem>
                        <SelectItem value="price-high">Price: High to Low</SelectItem>
                        <SelectItem value="price-low">Price: Low to High</SelectItem>
                        <SelectItem value="title-asc">Title: A to Z</SelectItem>
                        <SelectItem value="title-desc">Title: Z to A</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>

                  <div className="flex gap-3 flex-wrap">
                    <FilterPill
                      label="Active"
                      count={statusCounts.active}
                      isActive={showActive}
                      onClick={() => setShowActive(prev => !prev)}
                      variant="primary"
                      icon={<Circle className="fill-current" width={8} height={8} />}
                    />
                    <FilterPill
                      label="Sold Out"
                      count={statusCounts.soldOut}
                      isActive={showSoldOut}
                      onClick={() => setShowSoldOut(prev => !prev)}
                      variant="success"
                      icon={<Circle className="fill-current" width={8} height={8} />}
                    />
                    <FilterPill
                      label="Archived"
                      count={statusCounts.archived}
                      isActive={showArchived}
                      onClick={() => setShowArchived(prev => !prev)}
                      variant="neutral"
                      icon={<Circle className="fill-current" width={8} height={8} />}
                    />
                  </div>
                </div>
              </CollapsibleSection>
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="py-6">

          {/* Listings Grid */}
          <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3'}`}>
            {listingItems && listingItems.map((listing) => {
              return <EnhancedListingCard
                key={listing.id}
                listing={listing}
                onClick={() => handleEdit(listing)}
                onRecordSale={() => handleRecordSale(listing)}
                onEdit={() => handleEdit(listing)}
                onArchive={() => handleArchive(listing)}
                onDelete={() => handleDelete(listing)}
                readOnly={!hasListingAccess}
              />
            })}
          </div>

          {listingItems?.length === 0 && (
            <div className="w-full py-12 text-center bg-white dark:bg-neutral800 rounded-lg shadow-soft border border-neutral200 dark:border-neutral700">
              <div className="flex flex-col items-center gap-3">
                <BadgeIcon className="text-neutral300 dark:text-neutral600" width={48} height={48} />
                <p className="text-lg font-semibold text-neutral500 dark:text-neutral400">No listings found</p>
                <p className="text-sm text-neutral400 dark:text-neutral500">
                  {searchQuery ? `No listings match "${searchQuery}"` : "Create your first listing to get started"}
                </p>
              </div>
            </div>
          )}

        </div>
      </div>

      <NewListingWizard
        isShowing={showListingWizard}
        close={() => {
          setShowListingWizard(false)
          setSelectedListing(null)
        }}
        listing={selectedListing}
      />

      <RecordSaleDialog
        isShowing={showRecordSaleDialog}
        close={() => {
          setShowRecordSaleDialog(false)
          setSelectedListing(null)
        }}
        listing={selectedListing}
      />
    </Page>
  )
}

